import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getFinancialSummary,
  getDailySummary,
  getGoals,
  getCommitments,
  getCoachInsights,
} from '../Financial/FinancialService';
import {
  FinancialSummaryResponse,
  DailySpendSummaryResponse,
  GoalResponse,
  CommitmentResponse,
  CoachInsight,
} from '../Financial/types';
import PeriodSelector, {
  Period,
  shiftDate,
  isFutureRef,
  weekStart,
} from '../Financial/PeriodSelector';
import SpendingBarChart from '../Financial/SpendingBarChart';

// ─── design tokens matching docket-clickable-prototype.html ──────────────────
const C = {
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
  orange: '#ff8b32',
  needs: '#159b72',
  wants: '#EC4899',
  savings: '#3B82F6',
  muted: '#78869a',
  ink: '#17263d',
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

// ─── SpendStatusBadge ─────────────────────────────────────────────────────────
function SpendStatusBadge({ ratio }: { ratio: number }) {
  if (ratio <= 0.85) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 15, color: C.green }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: C.green }}>
          On track
        </Typography>
      </Box>
    );
  }
  if (ratio <= 1.0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <WarningAmberIcon sx={{ fontSize: 15, color: C.orange }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: C.orange }}>
          Close to target
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <ErrorOutlineIcon sx={{ fontSize: 15, color: C.red }} />
      <Typography variant="caption" fontWeight={700} sx={{ color: C.red }}>
        Over budget
      </Typography>
    </Box>
  );
}

// ─── SpendSummaryCard ─────────────────────────────────────────────────────────
interface SpendSummaryProps {
  summary: FinancialSummaryResponse | null;
  daily: DailySpendSummaryResponse | null;
  period: Period;
  refDate: Date;
  loading: boolean;
  customFrom?: string;
  customTo?: string;
}

function SpendSummaryCard({
  summary,
  daily,
  period,
  refDate,
  loading,
  customFrom,
  customTo,
}: SpendSummaryProps) {
  const { spent, target } = useMemo(() => {
    if (!summary) return { spent: 0, target: 0 };
    const totalSpent =
      (summary.needs ?? 0) + (summary.wants ?? 0) + (summary.savings ?? 0);
    const monthTarget = daily?.monthlyTotalTarget ?? summary.income ?? 0;

    if (period === 'month') return { spent: totalSpent, target: monthTarget };

    const daysInMonth = new Date(
      refDate.getFullYear(),
      refDate.getMonth() + 1,
      0,
    ).getDate();

    if (period === 'day') {
      const todayIso = new Date().toISOString().slice(0, 10);
      const dayEntry = daily?.entries?.find((e) => e.date === todayIso);
      return { spent: dayEntry?.total ?? 0, target: monthTarget / daysInMonth };
    }

    if (period === 'week') {
      const ws = weekStart(refDate);
      const we = new Date(ws);
      we.setDate(ws.getDate() + 6);
      const weekSpent = (daily?.entries ?? [])
        .filter((e) => {
          const d = new Date(e.date);
          return d >= ws && d <= we;
        })
        .reduce((s, e) => s + e.total, 0);
      return { spent: weekSpent, target: (monthTarget / daysInMonth) * 7 };
    }

    if (period === 'custom' && customFrom && customTo) {
      const rangeSpent = (daily?.entries ?? [])
        .filter((e) => e.date >= customFrom && e.date <= customTo)
        .reduce((s, e) => s + e.total, 0);
      const daysInRange = Math.max(
        Math.round(
          (new Date(customTo + 'T00:00:00').getTime() -
            new Date(customFrom + 'T00:00:00').getTime()) /
            86_400_000,
        ) + 1,
        1,
      );
      return {
        spent: rangeSpent,
        target: (monthTarget / daysInMonth) * daysInRange,
      };
    }

    return { spent: totalSpent, target: monthTarget };
  }, [summary, daily, period, refDate, customFrom, customTo]);

  const left = target - spent;
  const ratio = target > 0 ? spent / target : 0;
  const pct = Math.min(Math.round(ratio * 100), 100);
  const barColor = ratio <= 0.85 ? C.green : ratio <= 1.0 ? C.orange : C.red;

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={140} height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={200} height={44} sx={{ mb: 1.5 }} />
          <Skeleton
            variant="rectangular"
            height={8}
            sx={{ borderRadius: 4, mb: 2.5 }}
          />
          <Skeleton
            variant="rectangular"
            height={120}
            sx={{ borderRadius: 2 }}
          />
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No financial data yet. Add transactions or connect a bank account to
            get started.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Status + amounts */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box>
            <SpendStatusBadge ratio={ratio} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
                mt: 0.75,
              }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ color: C.ink, lineHeight: 1 }}
              >
                {gbp(spent)}
              </Typography>
              {target > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  of {gbp(target)}
                </Typography>
              )}
            </Box>
          </Box>
          {target > 0 && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                {left >= 0 ? 'remaining' : 'over budget'}
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  color: left >= 0 ? C.green : C.red,
                  lineHeight: 1.2,
                  mt: 0.25,
                }}
              >
                {gbp(Math.abs(left))}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Overall progress bar */}
        {target > 0 && (
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 8,
              borderRadius: 4,
              mb: 2.5,
              bgcolor: alpha(barColor, 0.12),
              '& .MuiLinearProgress-bar': {
                bgcolor: barColor,
                borderRadius: 4,
              },
            }}
          />
        )}

        {/* Bar chart */}
        {daily && (daily.entries?.length ?? 0) > 0 && (
          <SpendingBarChart
            entries={daily.entries ?? []}
            period={period}
            refDate={refDate}
            monthlyTarget={daily.monthlyTotalTarget}
            customFrom={customFrom}
            customTo={customTo}
          />
        )}

        {/* Per-category breakdown */}
        {summary.income > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
            {[
              {
                label: 'Needs',
                value: summary.needs,
                catTarget: summary.needsTarget,
                color: C.needs,
              },
              {
                label: 'Wants',
                value: summary.wants,
                catTarget: summary.wantsTarget,
                color: C.wants,
              },
              {
                label: 'Savings',
                value: summary.savings,
                catTarget: summary.savingsTarget,
                color: C.savings,
              },
            ].map(({ label, value, catTarget, color }) => {
              const budget = (summary.income * (catTarget ?? 0)) / 100;
              const isOver = budget > 0 && value > budget;
              const catPct =
                budget > 0
                  ? Math.min(Math.round((value / budget) * 100), 100)
                  : 0;
              return (
                <Box key={label} sx={{ flex: 1, minWidth: 80 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 0.4,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ color: C.ink }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: isOver ? C.red : C.muted }}
                    >
                      {gbp(value)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={catPct}
                    sx={{
                      height: 5,
                      borderRadius: 3,
                      bgcolor: alpha(color, 0.12),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: isOver ? C.red : color,
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── GoalPreviewCard ──────────────────────────────────────────────────────────
function GoalPreviewCard({
  goals,
  loading,
}: {
  goals: GoalResponse[];
  loading: boolean;
}) {
  if (loading)
    return (
      <Skeleton
        variant="rectangular"
        height={96}
        sx={{ borderRadius: 2, mb: 3 }}
      />
    );
  const top = goals.find((g) => g.status === 'ACTIVE');
  if (!top) return null;

  return (
    <Card
      component="a"
      href="/user/goals"
      sx={{
        mb: 3,
        textDecoration: 'none',
        display: 'block',
        '&:hover': { boxShadow: '0 16px 40px rgba(22,54,93,0.12)' },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ color: C.ink }}>
            {top.name}
          </Typography>
          <Chip
            label={`${top.progressPercent}%`}
            size="small"
            sx={{
              bgcolor: alpha(C.blue, 0.1),
              color: C.blue,
              fontWeight: 700,
              fontSize: '0.7rem',
            }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(top.progressPercent, 100)}
          sx={{
            height: 6,
            borderRadius: 3,
            mb: 1,
            bgcolor: alpha(C.blue, 0.12),
            '& .MuiLinearProgress-bar': { bgcolor: C.blue, borderRadius: 3 },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            {gbp(top.currentAmount)} saved
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Target: {gbp(top.targetAmount)}
          </Typography>
        </Box>
        {goals.length > 1 && (
          <Typography
            variant="caption"
            sx={{ color: C.muted, mt: 0.5, display: 'block' }}
          >
            +{goals.length - 1} more goal{goals.length > 2 ? 's' : ''}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── CommitmentsPreviewCard ────────────────────────────────────────────────────
function CommitmentsPreviewCard({
  commitments,
  loading,
}: {
  commitments: CommitmentResponse[];
  loading: boolean;
}) {
  if (loading)
    return (
      <Skeleton
        variant="rectangular"
        height={88}
        sx={{ borderRadius: 2, mb: 3 }}
      />
    );
  const active = commitments.filter((c) => c.isActive).slice(0, 3);
  if (active.length === 0) return null;

  return (
    <Card
      component="a"
      href="/user/commitments"
      sx={{
        mb: 3,
        textDecoration: 'none',
        display: 'block',
        '&:hover': { boxShadow: '0 16px 40px rgba(22,54,93,0.12)' },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ color: C.ink }}>
            Upcoming commitments
          </Typography>
          <Typography variant="caption" sx={{ color: C.blue, fontWeight: 600 }}>
            View all →
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {active.map((c) => (
            <Box
              key={c.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: C.ink }}>
                {c.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {c.nextDueDate && (
                  <Typography variant="caption" sx={{ color: C.muted }}>
                    {new Date(c.nextDueDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: C.ink }}
                >
                  {gbp(c.amount)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── CoachInsightCard ─────────────────────────────────────────────────────────
const SEVERITY_COLOUR: Record<string, string> = {
  CRITICAL: '#ff454d',
  HIGH: '#ff8b32',
  MEDIUM: '#1475d1',
  LOW: '#159b72',
};

function CoachInsightCard({
  insights,
  loading,
}: {
  insights: CoachInsight[];
  loading: boolean;
}) {
  if (loading)
    return (
      <Skeleton
        variant="rectangular"
        height={76}
        sx={{ borderRadius: 2, mb: 3 }}
      />
    );
  const top = insights[0];
  if (!top) return null;

  const colour = SEVERITY_COLOUR[top.severity] ?? C.blue;

  return (
    <Card
      component="a"
      href="/user/coach"
      sx={{
        mb: 3,
        textDecoration: 'none',
        display: 'block',
        borderLeft: `4px solid ${colour}`,
        '&:hover': { boxShadow: '0 16px 40px rgba(22,54,93,0.12)' },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <TrendingUpIcon
            sx={{ color: colour, mt: 0.25, fontSize: 20, flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: C.ink, mb: 0.25 }}
            >
              {top.title}
            </Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {top.summary}
            </Typography>
          </Box>
          <Chip
            label={top.severity}
            size="small"
            sx={{
              flexShrink: 0,
              bgcolor: alpha(colour, 0.1),
              color: colour,
              fontWeight: 700,
              fontSize: '0.6rem',
              height: 20,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── HomePage ─────────────────────────────────────────────────────────────────
const HomePage = (): JSX.Element => {
  const now = useMemo(() => new Date(), []);

  const [period, setPeriod] = useState<Period>('month');
  const [refDate, setRefDate] = useState<Date>(now);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const year =
    period === 'custom' && customFrom
      ? parseInt(customFrom.slice(0, 4), 10)
      : refDate.getFullYear();
  const month =
    period === 'custom' && customFrom
      ? parseInt(customFrom.slice(5, 7), 10)
      : refDate.getMonth() + 1;

  const [summary, setSummary] = useState<FinancialSummaryResponse | null>(null);
  const [daily, setDaily] = useState<DailySpendSummaryResponse | null>(null);
  const [goals, setGoals] = useState<GoalResponse[]>([]);
  const [commitments, setCommitments] = useState<CommitmentResponse[]>([]);
  const [insights, setInsights] = useState<CoachInsight[]>([]);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [loadingComm, setLoadingComm] = useState(true);
  const [loadingCoach, setLoadingCoach] = useState(true);
  const [summaryError, setSummaryError] = useState('');

  const displayName =
    localStorage.getItem('displayName') || localStorage.getItem('email') || '';
  const firstName = displayName.includes('@')
    ? displayName.split('@')[0]
    : displayName.split(' ')[0];

  // Financial summary + daily chart — re-fetch when month/year changes
  useEffect(() => {
    setLoadingSummary(true);
    setSummaryError('');
    Promise.all([
      getFinancialSummary(year, month),
      getDailySummary(year, month),
    ])
      .then(([sumRes, dailyRes]) => {
        setSummary(sumRes.data ?? null);
        setDaily(dailyRes.data ?? null);
      })
      .catch(() =>
        setSummaryError('Could not load financial data. Please try again.'),
      )
      .finally(() => setLoadingSummary(false));
  }, [year, month]);

  // Goals, commitments, coach — fetched once on mount
  useEffect(() => {
    getGoals('ACTIVE')
      .then((r) => setGoals(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoadingGoals(false));

    getCommitments()
      .then((r) => setCommitments(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoadingComm(false));

    getCoachInsights()
      .then((r) => setInsights(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoadingCoach(false));
  }, []);

  const handlePrev = () => setRefDate((d) => shiftDate(period, d, -1));
  const handleNext = () => {
    const next = shiftDate(period, refDate, 1);
    if (!isFutureRef(period, next, now)) setRefDate(next);
  };
  const handlePeriodChange = (p: Period) => {
    setPeriod(p);
    if (p !== 'custom') setRefDate(new Date(now));
  };
  const handleCustomApply = (from: string, to: string) => {
    setCustomFrom(from);
    setCustomTo(to);
  };

  return (
    <UserDashboard>
      <Box
        sx={{
          background: `linear-gradient(175deg, ${alpha('#1475d1', 0.05)} 0%, transparent 40%)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Greeting */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
              {firstName ? `Hi, ${firstName}` : 'Your Docket'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Here's your financial overview
            </Typography>
          </Box>

          {/* Period selector */}
          <PeriodSelector
            period={period}
            refDate={refDate}
            now={now}
            onPeriodChange={handlePeriodChange}
            onPrev={handlePrev}
            onNext={handleNext}
            customFrom={customFrom}
            customTo={customTo}
            onCustomApply={handleCustomApply}
          />

          {/* API error */}
          {summaryError && !loadingSummary && (
            <Card sx={{ mb: 3, border: `1px solid ${alpha(C.red, 0.3)}` }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="body2" color="error">
                  {summaryError}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Main spending summary + chart */}
          <SpendSummaryCard
            summary={summary}
            daily={daily}
            period={period}
            refDate={refDate}
            loading={loadingSummary}
            customFrom={customFrom}
            customTo={customTo}
          />

          {/* Top active goal */}
          <GoalPreviewCard goals={goals} loading={loadingGoals} />

          {/* Upcoming commitments */}
          <CommitmentsPreviewCard
            commitments={commitments}
            loading={loadingComm}
          />

          {/* Top coach insight */}
          <CoachInsightCard insights={insights} loading={loadingCoach} />

          {/* Quick-access links */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              {
                label: 'Money',
                href: '/user/money',
                desc: 'Income, spending & ratios',
              },
              {
                label: 'Transactions',
                href: '/user/transactions',
                desc: 'View & categorise',
              },
              {
                label: 'Coach',
                href: '/user/coach',
                desc: 'Insights & actions',
              },
            ].map((link) => (
              <Grid item xs={12} sm={4} key={link.label}>
                <Card
                  component="a"
                  href={link.href}
                  sx={{
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 16px 40px rgba(22,54,93,0.12)' },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      sx={{ color: C.ink }}
                    >
                      {link.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {link.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </UserDashboard>
  );
};

export default HomePage;
